import React from "react";
import moment from "moment";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";

import WithAppLayout from "../../components/WithAppLayout";
import PageTitle from "../../components/PageTitle";
import { LoadingIndicator } from "../../components/LoadingIndicator";
import colors from "../../helpers/colors";
import { isMoblyzeAdmin } from "../../helpers/permissions";
import { SERVICE_MAILBOXES_PATH } from "../../routes";
import {
  disconnectConnection,
  listServiceAccounts,
  startServiceAccountConnect,
} from "../../services/clientMailApi";

// Hidden super-user page: connects a Microsoft 365 mailbox to a service
// account (e.g. service:github-actions-moblyze-ops) through the
// moblyze-client-mail Worker, so automation can run on that mailbox: a shared
// inbox or a person's. Not linked from any nav.

// Query keys the Worker appends when it redirects back after Microsoft sign-in.
const RETURN_PARAMS = ["mailbox", "provider", "address", "reason"];

export const RETURN_REASON_MESSAGES = {
  mailbox_mismatch:
    "The Microsoft account that signed in is not the mailbox you selected. Connect again and sign in as that mailbox (choose 'Use another account' if your own account is picked automatically).",
  mailbox_not_allowed:
    "That mailbox is not allowed for service accounts. Add it to SERVICE_MAILBOXES in the Worker's wrangler.toml first.",
  access_denied:
    "Microsoft sign-in was cancelled or the requested permissions were not granted.",
  session_mismatch:
    "The sign-in did not match the session that started it (it may have expired or been opened in another browser). Start the connection again from this page.",
  token_exchange_failed:
    "Microsoft sign-in finished, but the mail service could not obtain tokens for the mailbox. Try again; if it keeps failing, check the Worker logs.",
};

export function describeReturnReason(reason) {
  return RETURN_REASON_MESSAGES[reason] || `Connection failed: ${reason || "unknown error"}`;
}

// What the Worker's redirect told us, or null when this isn't a return visit.
function readReturn(searchParams) {
  const outcome = searchParams.get("mailbox");
  if (outcome === "connected") {
    return { status: "success", address: searchParams.get("address") || "" };
  }
  if (outcome === "error") {
    return { status: "error", reason: searchParams.get("reason") || "" };
  }
  return null;
}

const STATUS_COLORS = {
  Connected: "green",
  NeedsReauth: "orange",
  Revoked: "red",
};

const STATUS_LABELS = {
  Connected: "Connected",
  NeedsReauth: "Needs re-auth",
  Revoked: "Revoked",
};

function formatDate(value) {
  if (!value) return "—";
  const m = moment(value);
  return m.isValid() ? m.format("D MMM YYYY, HH:mm") : "—";
}

function ReturnBanner({ result, onClose }) {
  if (!result) return null;
  const success = result.status === "success";
  return (
    <Alert status={success ? "success" : "error"} borderRadius="md" color="gray.800">
      <AlertIcon />
      <AlertDescription flex="1">
        {success
          ? `Connected ${result.address || "the mailbox"}.`
          : describeReturnReason(result.reason)}
      </AlertDescription>
      <Button size="xs" variant="ghost" onClick={onClose}>
        Dismiss
      </Button>
    </Alert>
  );
}

function ConnectionCard({ connection, onDisconnect }) {
  return (
    <Box
      border="1px solid"
      borderColor="gray.700"
      borderRadius="md"
      p={4}
      bg="gray.900"
      data-testid="service-mailbox-connection"
    >
      <Flex justify="space-between" align="flex-start" gap={4} wrap="wrap">
        <Stack spacing={2}>
          <Text fontSize="xl" fontWeight="bold" color="white" wordBreak="break-all">
            {connection.mailboxAddress}
          </Text>
          <HStack spacing={2} wrap="wrap">
            <Badge colorScheme={STATUS_COLORS[connection.status] || "gray"}>
              {STATUS_LABELS[connection.status] || connection.status}
            </Badge>
            {(connection.grants || []).map((grant) => (
              <Badge key={grant} variant="outline" colorScheme="gray" color="gray.300">
                {grant}
              </Badge>
            ))}
          </HStack>
          <Text fontSize="sm" color="gray.400">
            Connected {formatDate(connection.connectedAt)} · Updated{" "}
            {formatDate(connection.updatedAt)}
          </Text>
          {connection.lastError ? (
            <Text fontSize="sm" color="red.300">
              Last error: {connection.lastError}
            </Text>
          ) : null}
        </Stack>
        <Button
          size="sm"
          variant="outline"
          colorScheme="red"
          onClick={() => onDisconnect(connection)}
        >
          Disconnect
        </Button>
      </Flex>
    </Box>
  );
}

function DisconnectModal({ connection, busy, onCancel, onConfirm }) {
  return (
    <Modal isOpen={!!connection} onClose={busy ? () => {} : onCancel} isCentered>
      <ModalOverlay />
      {/* The portal is dark: Chakra's default white modal would show its white text on white. */}
      <ModalContent bg={colors.greyDark} color={colors.white} mx={4}>
        <ModalHeader>Disconnect mailbox?</ModalHeader>
        <ModalCloseButton isDisabled={busy} />
        <ModalBody>
          <Text color="gray.300">
            {connection?.mailboxAddress} will stop sending and receiving for this
            service account until it is connected again.
          </Text>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" colorScheme="whiteAlpha" color={colors.white} mr={3} onClick={onCancel} isDisabled={busy}>
            Cancel
          </Button>
          <Button colorScheme="red" onClick={onConfirm} isLoading={busy}>
            Confirm disconnect
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

const defaultNavigate = (url) => window.location.assign(url);

// Declared at module scope: the app layout re-renders the page on every
// Sendbird SSE poll, and a component type recreated per render would remount
// and lose the selection / in-flight state.
export function ServiceMailboxesPageInner({ currentUser, navigate = defaultNavigate }) {
  const isAdmin = isMoblyzeAdmin(currentUser);
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [returnResult, setReturnResult] = React.useState(() => readReturn(searchParams));

  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState(null);
  const [selectedAccount, setSelectedAccount] = React.useState("");
  const [selectedMailbox, setSelectedMailbox] = React.useState("");
  const [connecting, setConnecting] = React.useState(false);
  const [connectError, setConnectError] = React.useState(null);
  const [pendingDisconnect, setPendingDisconnect] = React.useState(null);
  const [disconnecting, setDisconnecting] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const result = await listServiceAccounts();
      setData(result);
      const accounts = result?.serviceAccounts || [];
      setSelectedAccount((current) =>
        accounts.some((a) => a.name === current) ? current : accounts[0]?.name || ""
      );
      const mailboxes = result?.allowedMailboxes || [];
      setSelectedMailbox((current) =>
        mailboxes.includes(current) ? current : mailboxes[0] || ""
      );
    } catch (err) {
      setLoadError(err?.message || "Could not load service accounts.");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  // Once the return banner has been captured, strip the Worker's params so a
  // reload doesn't show it again.
  React.useEffect(() => {
    if (!RETURN_PARAMS.some((key) => searchParams.has(key))) return;
    const next = new URLSearchParams(searchParams);
    RETURN_PARAMS.forEach((key) => next.delete(key));
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  if (!isAdmin) {
    return null;
  }

  const accounts = data?.serviceAccounts || [];
  const allowedMailboxes = data?.allowedMailboxes || [];
  const account = accounts.find((a) => a.name === selectedAccount);
  const connections = account?.connections || [];

  const handleConnect = async () => {
    if (!selectedAccount || !selectedMailbox || connecting) return;
    setConnecting(true);
    setConnectError(null);
    try {
      const { authorizeUrl } = await startServiceAccountConnect({
        serviceAccount: selectedAccount,
        expectedMailbox: selectedMailbox,
        returnPath: `${window.location.origin}${SERVICE_MAILBOXES_PATH}`,
      });
      navigate(authorizeUrl);
      // Leave the button disabled: the browser is on its way to Microsoft.
    } catch (err) {
      setConnectError(err?.message || "Could not start the connection.");
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!pendingDisconnect) return;
    setDisconnecting(true);
    try {
      await disconnectConnection(pendingDisconnect.id);
      toast({
        title: `Disconnected ${pendingDisconnect.mailboxAddress}`,
        status: "success",
      });
      setPendingDisconnect(null);
      await load();
    } catch (err) {
      toast({
        title: "Could not disconnect the mailbox",
        description: err?.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <Stack spacing={6} maxW="3xl">
      <Stack spacing={1}>
        <PageTitle title="Service mailboxes" />
        <Text color="gray.400">
          Connect a Microsoft 365 mailbox to a service account so automations
          can send from and work with that mailbox.
        </Text>
      </Stack>

      <ReturnBanner result={returnResult} onClose={() => setReturnResult(null)} />

      {loadError ? (
        <Alert status="error" borderRadius="md" color="gray.800">
          <AlertIcon />
          <AlertDescription flex="1">{loadError}</AlertDescription>
          <Button size="xs" onClick={load}>
            Retry
          </Button>
        </Alert>
      ) : null}

      {loading && !data ? <LoadingIndicator /> : null}

      {data ? (
        <>
          {accounts.length === 0 ? (
            <Text color="gray.300">No service accounts are configured in the mail service.</Text>
          ) : (
            <FormControl>
              <FormLabel htmlFor="service-account" color="white">
                Service account
              </FormLabel>
              <Select
                id="service-account"
                color="white"
                value={selectedAccount}
                onChange={(e) => setSelectedAccount(e.target.value)}
              >
                {accounts.map((a) => (
                  <option key={a.name} value={a.name}>
                    {a.name}
                  </option>
                ))}
              </Select>
            </FormControl>
          )}

          {account ? (
            <Stack spacing={3}>
              <Text fontWeight="semibold" color="white">
                Current connection
              </Text>
              {connections.length === 0 ? (
                <Text color="gray.400">No mailbox is connected to {account.name}.</Text>
              ) : (
                connections.map((connection) => (
                  <ConnectionCard
                    key={connection.id}
                    connection={connection}
                    onDisconnect={setPendingDisconnect}
                  />
                ))
              )}
            </Stack>
          ) : null}

          {account ? (
            <Stack
              spacing={4}
              border="1px solid"
              borderColor="gray.700"
              borderRadius="md"
              p={4}
            >
              <Text fontWeight="semibold" color="white">
                Connect a mailbox
              </Text>
              {allowedMailboxes.length === 0 ? (
                <Text color="gray.300">
                  No mailboxes are allowed for service accounts yet. Set
                  SERVICE_MAILBOXES in the moblyze-client-mail Worker's
                  wrangler.toml and redeploy it.
                </Text>
              ) : (
                <>
                  <FormControl>
                    <FormLabel htmlFor="service-mailbox" color="white">
                      Mailbox
                    </FormLabel>
                    <Select
                      id="service-mailbox"
                      color="white"
                      value={selectedMailbox}
                      onChange={(e) => setSelectedMailbox(e.target.value)}
                    >
                      {allowedMailboxes.map((mailbox) => (
                        <option key={mailbox} value={mailbox}>
                          {mailbox}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                  <Alert status="warning" borderRadius="md" color="gray.800">
                    <AlertIcon />
                    <AlertDescription>
                      The person signing in to Microsoft must sign in as this
                      mailbox. If your own Microsoft account is signed in in this
                      browser, choose 'Use another account'.
                    </AlertDescription>
                  </Alert>
                  {connectError ? (
                    <Text color="red.300" role="alert">
                      {connectError}
                    </Text>
                  ) : null}
                  <Box>
                    <Button
                      colorScheme="teal"
                      onClick={handleConnect}
                      isDisabled={connecting || !selectedMailbox}
                      isLoading={connecting}
                      loadingText="Connecting"
                    >
                      Connect
                    </Button>
                  </Box>
                </>
              )}
            </Stack>
          ) : null}
        </>
      ) : null}

      <DisconnectModal
        connection={pendingDisconnect}
        busy={disconnecting}
        onCancel={() => setPendingDisconnect(null)}
        onConfirm={handleDisconnect}
      />
    </Stack>
  );
}

function PageComponent({ currentUser }) {
  return <ServiceMailboxesPageInner currentUser={currentUser} />;
}

export default WithAppLayout(PageComponent);
